import type { PlanModel, Point } from "./model";
import { wallEndpoints } from "./model";
import { formatLength, PU_PER_FOOT, PU_PER_INCH } from "./units";
import type { ScenePrimitive, StyleToken } from "./scene";

/**
 * THE ANNOTATION LAYER (spec §83).
 *
 *   MODEL GEOMETRY  = what the building is
 *   ANNOTATIONS     = how we communicate it
 *
 * A dimension does not define a wall; it REPORTS one. So a dimension
 * stores REFERENCES to the geometry it measures and recomputes its own
 * value — move the wall and the dimension follows, because the number was
 * never typed in the first place. Dragging the dimension line moves only
 * where it is drawn.
 *
 * Everything here is pure and produces the same ScenePrimitives the
 * canvas and the printed sheet already render, so there is one drawing
 * path and no second renderer to keep in step.
 */

export type AnnotationKind = "dimension" | "leader" | "text";

/** What a dimension end is attached to. */
export type AnchorKind = "wall" | "opening" | "object" | "point";

/**
 * Which line of a wall is being measured. A wall is 4.5" thick, so
 * "inside face to inside face" and "centre to centre" are different
 * numbers — the drawing must say which it means (spec §83-4).
 */
export type WallReference = "center" | "near-face" | "far-face";

export interface AnnotationAnchor {
  kind: AnchorKind;
  /** The wall / opening / object measured. Null for a free point. */
  targetId: string | null;
  reference: WallReference;
  /** Fallback position, and the only position for a free point (pu). */
  at: Point;
}

export type LineWeight = "thin" | "normal" | "thick";
export type DimensionPrecision = "inch" | "half" | "quarter" | "exact";

export interface Annotation {
  id: string;
  designId: string;
  floorId: string;
  kind: AnnotationKind;
  a: AnnotationAnchor;
  /** Dimensions measure a to b; a leader points at `a` only. */
  b: AnnotationAnchor | null;
  /** Where the dimension line or the text sits, relative to the anchor. */
  offset: Point;
  text: string;
  fontScale: number;
  lineWeight: LineWeight;
  precision: DimensionPrecision;
  sortOrder: number;
}

export const DEFAULT_ANNOTATION: Pick<Annotation, "fontScale" | "lineWeight" | "precision"> = {
  fontScale: 1,
  lineWeight: "normal",
  precision: "inch",
};

export const LINE_WEIGHT_PX: Readonly<Record<LineWeight, number>> = {
  thin: 1,
  normal: 1.6,
  thick: 2.6,
};

/* ------------------------------------------------------------------ *
 * Resolving an anchor against live geometry
 * ------------------------------------------------------------------ */

const sub = (a: Point, b: Point): Point => ({ x: a.x - b.x, y: a.y - b.y });
const add = (a: Point, b: Point): Point => ({ x: a.x + b.x, y: a.y + b.y });
const scale = (p: Point, k: number): Point => ({ x: p.x * k, y: p.y * k });
const len = (p: Point): number => Math.hypot(p.x, p.y);
const norm = (p: Point): Point => {
  const l = len(p);
  return l === 0 ? { x: 0, y: 0 } : { x: p.x / l, y: p.y / l };
};
const dot = (a: Point, b: Point): number => a.x * b.x + a.y * b.y;

export interface ResolvedAnchor {
  point: Point;
  /** False when the geometry it named is gone (spec §83-10/37). */
  resolved: boolean;
  /** The wall's own direction, where the anchor is a wall. */
  direction: Point | null;
}

/**
 * Where an anchor actually is, right now. A reference to geometry that no
 * longer exists resolves to its last known point and says so — the
 * annotation is never silently re-pointed at something else.
 */
export function resolveAnchor(model: PlanModel, anchor: AnnotationAnchor, toward: Point | null): ResolvedAnchor {
  if (anchor.kind === "wall" && anchor.targetId !== null) {
    const wall = model.walls.get(anchor.targetId);
    if (!wall || wall.archivedAt !== null) return { point: anchor.at, resolved: false, direction: null };
    const [a, b] = wallEndpoints(model, wall);
    const dir = norm(sub({ x: b.x, y: b.y }, { x: a.x, y: a.y }));
    const normal: Point = { x: -dir.y, y: dir.x };
    // The point on the wall's own line nearest the other end of the
    // dimension, so a wall-to-wall measurement is taken square across.
    const from: Point = { x: a.x, y: a.y };
    const t = toward ? dot(sub(toward, from), dir) : dot(sub(anchor.at, from), dir);
    const clamped = Math.max(0, Math.min(len(sub({ x: b.x, y: b.y }, from)), t));
    const online = add(from, scale(dir, clamped));
    if (anchor.reference === "center" || wall.thickness === 0) {
      return { point: online, resolved: true, direction: dir };
    }
    // A face is half a thickness off the centreline; which face is the one
    // FACING the other end of the dimension (§83-4).
    const half = wall.thickness / 2;
    const side = toward ? Math.sign(dot(sub(toward, online), normal)) || 1 : 1;
    const sign = anchor.reference === "near-face" ? side : -side;
    return { point: add(online, scale(normal, half * sign)), resolved: true, direction: dir };
  }
  if (anchor.kind === "opening" && anchor.targetId !== null) {
    const opening = model.openings.get(anchor.targetId);
    if (!opening || opening.archivedAt !== null) return { point: anchor.at, resolved: false, direction: null };
    const wall = model.walls.get(opening.wallId);
    if (!wall || wall.archivedAt !== null) return { point: anchor.at, resolved: false, direction: null };
    const [a, b] = wallEndpoints(model, wall);
    const dir = norm(sub({ x: b.x, y: b.y }, { x: a.x, y: a.y }));
    const at = add({ x: a.x, y: a.y }, scale(dir, opening.offsetFromStart + opening.width / 2));
    return { point: at, resolved: true, direction: dir };
  }
  if (anchor.kind === "object" && anchor.targetId !== null) {
    const object = model.objects.get(anchor.targetId);
    if (!object || object.archivedAt !== null) return { point: anchor.at, resolved: false, direction: null };
    return { point: { x: object.x, y: object.y }, resolved: true, direction: null };
  }
  return { point: anchor.at, resolved: true, direction: null };
}

/* ------------------------------------------------------------------ *
 * Measurement
 * ------------------------------------------------------------------ */

const PRECISION_PU: Readonly<Record<DimensionPrecision, number>> = {
  inch: PU_PER_INCH,
  half: PU_PER_INCH / 2,
  quarter: PU_PER_INCH / 4,
  exact: 1,
};

/** Round a measured length to the drawing's stated precision. */
export function roundToPrecision(pu: number, precision: DimensionPrecision): number {
  const step = PRECISION_PU[precision];
  return Math.round(pu / step) * step;
}

export interface DimensionMeasurement {
  /** The measured distance in plan units, at the stated precision. */
  lengthPu: number;
  /** "12' 6"" — the project's own length wording, never a second system. */
  label: string;
  from: Point;
  to: Point;
  /** False when either end named geometry that has gone. */
  resolved: boolean;
}

/**
 * What a dimension currently reads. Recomputed from geometry every time,
 * which is what makes it associative (spec §83-3).
 */
export function measureDimension(model: PlanModel, annotation: Annotation): DimensionMeasurement | null {
  if (annotation.kind !== "dimension" || annotation.b === null) return null;
  // Each end is resolved TOWARD the other, so faces pick the sides that
  // look at each other and wall points are taken square across.
  const roughA = resolveAnchor(model, annotation.a, null);
  const roughB = resolveAnchor(model, annotation.b, null);
  const a = resolveAnchor(model, annotation.a, roughB.point);
  const b = resolveAnchor(model, annotation.b, roughA.point);
  const raw = len(sub(b.point, a.point));
  const lengthPu = roundToPrecision(raw, annotation.precision);
  return {
    lengthPu,
    label: formatLength(lengthPu),
    from: a.point,
    to: b.point,
    resolved: a.resolved && b.resolved,
  };
}

/* ------------------------------------------------------------------ *
 * Drawing
 * ------------------------------------------------------------------ */

const BASE_TEXT_PX = 12;

function tick(at: Point, dir: Point, style: StyleToken, widthPx: number, sizePu: number): ScenePrimitive {
  // An architectural tick: a short slash through the dimension line.
  const d = scale(norm({ x: dir.x + dir.y, y: dir.y - dir.x }), sizePu / 2);
  return { type: "line", from: sub(at, d), to: add(at, d), style, widthPx };
}

/**
 * A dimension as it draws: extension lines from the geometry, the
 * dimension line offset to where the user put it, ticks at each end and
 * the measurement centred on it.
 */
export function dimensionPrimitives(
  model: PlanModel,
  annotation: Annotation,
  options: { selected?: boolean; tickPu?: number } = {},
): ScenePrimitive[] {
  const measure = measureDimension(model, annotation);
  if (!measure) return [];
  const style: StyleToken = options.selected ? "wall-selected" : measure.resolved ? "dimension" : "dimension-unresolved";
  const widthPx = LINE_WEIGHT_PX[annotation.lineWeight];
  const axis = norm(sub(measure.to, measure.from));
  const normal: Point = { x: -axis.y, y: axis.x };
  // The offset is stored along the dimension's own normal, so the line
  // keeps its distance when the geometry moves (spec §83-5).
  const shift = scale(normal, dot(annotation.offset, normal));
  const a = add(measure.from, shift);
  const b = add(measure.to, shift);
  const tickPu = options.tickPu ?? 4 * PU_PER_INCH;
  const out: ScenePrimitive[] = [
    // Extension lines, from the geometry out past the dimension line.
    { type: "line", from: measure.from, to: add(a, scale(normal, dot(shift, normal) >= 0 ? tickPu / 2 : -tickPu / 2)), style, widthPx: Math.max(1, widthPx - 0.6) },
    { type: "line", from: measure.to, to: add(b, scale(normal, dot(shift, normal) >= 0 ? tickPu / 2 : -tickPu / 2)), style, widthPx: Math.max(1, widthPx - 0.6) },
    { type: "line", from: a, to: b, style, widthPx },
    tick(a, axis, style, widthPx, tickPu),
    tick(b, axis, style, widthPx, tickPu),
  ];
  const mid = scale(add(a, b), 0.5);
  let angle = Math.atan2(axis.y, axis.x);
  // Never print a dimension upside down.
  if (angle > Math.PI / 2 || angle < -Math.PI / 2) angle += Math.PI;
  out.push({
    type: "text",
    at: mid,
    text: measure.resolved ? measure.label : `${measure.label} (?)`,
    style,
    sizePx: BASE_TEXT_PX * annotation.fontScale,
    offsetPx: [0, -6 * annotation.fontScale],
    angleRad: angle,
  });
  return out;
}

/** A leader note: the thing it points at, the elbow, and the words. */
export function leaderPrimitives(
  model: PlanModel,
  annotation: Annotation,
  options: { selected?: boolean } = {},
): ScenePrimitive[] {
  const target = resolveAnchor(model, annotation.a, null);
  const style: StyleToken = options.selected ? "wall-selected" : target.resolved ? "annotation" : "dimension-unresolved";
  const widthPx = LINE_WEIGHT_PX[annotation.lineWeight];
  const textAt = add(target.point, annotation.offset);
  // A short horizontal shoulder under the text, the way a note is drawn.
  const shoulderPu = 12 * PU_PER_INCH * annotation.fontScale;
  const towardText = annotation.offset.x >= 0 ? 1 : -1;
  const elbow: Point = { x: textAt.x - shoulderPu * towardText, y: textAt.y };
  const out: ScenePrimitive[] = [
    { type: "line", from: target.point, to: elbow, style, widthPx },
    { type: "line", from: elbow, to: textAt, style, widthPx },
    { type: "marker", at: target.point, radiusPx: 3, style, shape: "circle" },
  ];
  const lines = annotation.text.split("\n");
  lines.forEach((line, index) => {
    out.push({
      type: "text",
      at: textAt,
      text: target.resolved ? line : index === 0 ? `${line} (unresolved)` : line,
      style,
      sizePx: BASE_TEXT_PX * annotation.fontScale,
      offsetPx: [towardText > 0 ? 4 : -4, -2 + index * 13 * annotation.fontScale],
    });
  });
  return out;
}

/** A plain note: words on the drawing, anchored where they were put. */
export function textPrimitives(annotation: Annotation, options: { selected?: boolean } = {}): ScenePrimitive[] {
  const style: StyleToken = options.selected ? "wall-selected" : "annotation";
  const at = add(annotation.a.at, annotation.offset);
  return annotation.text.split("\n").map((line, index) => ({
    type: "text" as const,
    at,
    text: line,
    style,
    sizePx: BASE_TEXT_PX * annotation.fontScale,
    offsetPx: [0, index * 13 * annotation.fontScale] as [number, number],
  }));
}

/** Every annotation on a floor, as drawing primitives. */
export function annotationPrimitives(
  model: PlanModel,
  annotations: readonly Annotation[],
  selectedId: string | null = null,
): ScenePrimitive[] {
  const out: ScenePrimitive[] = [];
  for (const annotation of [...annotations].sort((a, b) => a.sortOrder - b.sortOrder)) {
    const selected = annotation.id === selectedId;
    if (annotation.kind === "dimension") out.push(...dimensionPrimitives(model, annotation, { selected }));
    else if (annotation.kind === "leader") out.push(...leaderPrimitives(model, annotation, { selected }));
    else out.push(...textPrimitives(annotation, { selected }));
  }
  return out;
}

/* ------------------------------------------------------------------ *
 * Selection
 * ------------------------------------------------------------------ */

const distanceToSegment = (p: Point, a: Point, b: Point): number => {
  const ab = sub(b, a);
  const l2 = dot(ab, ab);
  if (l2 === 0) return len(sub(p, a));
  const t = Math.max(0, Math.min(1, dot(sub(p, a), ab) / l2));
  return len(sub(p, add(a, scale(ab, t))));
};

/**
 * The annotation under the cursor, nearest first. Annotations are picked
 * before geometry in the annotation tools and after it in the drawing
 * tools, so neither gets in the other's way (spec §83-29).
 */
export function annotationAt(
  model: PlanModel,
  annotations: readonly Annotation[],
  point: Point,
  tolerancePu: number,
): Annotation | null {
  let best: { annotation: Annotation; distance: number } | null = null;
  const consider = (annotation: Annotation, distance: number) => {
    if (distance > tolerancePu) return;
    if (best === null || distance < best.distance) best = { annotation, distance };
  };
  for (const annotation of annotations) {
    if (annotation.kind === "dimension") {
      const measure = measureDimension(model, annotation);
      if (!measure) continue;
      const axis = norm(sub(measure.to, measure.from));
      const normal: Point = { x: -axis.y, y: axis.x };
      const shift = scale(normal, dot(annotation.offset, normal));
      consider(annotation, distanceToSegment(point, add(measure.from, shift), add(measure.to, shift)));
      continue;
    }
    const anchor = resolveAnchor(model, annotation.a, null);
    const textAt = add(annotation.kind === "leader" ? anchor.point : annotation.a.at, annotation.offset);
    consider(annotation, len(sub(point, textAt)));
    if (annotation.kind === "leader") consider(annotation, distanceToSegment(point, anchor.point, textAt));
  }
  return best === null ? null : (best as { annotation: Annotation }).annotation;
}

/** Feet-and-inches, borrowed from the one length formatter (spec §83-7). */
export const formatDimension = (pu: number, precision: DimensionPrecision): string =>
  formatLength(roundToPrecision(pu, precision));

export const feetFromPu = (pu: number): number => pu / PU_PER_FOOT;
